import { getFeedback } from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyleSheetManager } from 'styled-components';

import { Organization, OrganizationStatus } from 'dashboard/models/organization';
import { Z_INDICES } from 'dashboard/styles/variables';

import SupportChatHelper, { SupportChatHelperProps } from './SupportChat';

export * from './SupportChat';

// The Shadow DOM is used to provide a scoped CSS and DOM tree,
// preventing styles and elements from leaking in or out of the component.
// This needs to be in persona-web instead of the @persona-id/support-chat package
// because it ensures that the integration and customization specific to persona-web
// are maintained without affecting the core support chat functionality
// provided by the @persona-id/support-chat package.
export default function SupportChatShadowDOM({
  organization,
  ...rest
}: Omit<SupportChatHelperProps, 'isActiveOrganization' | 'customReportBugCallback'> & {
  organization: Organization;
}) {
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  // Integration is null if not installed in current hub
  const sentryFeedbackIntegration = getFeedback();

  useEffect(() => {
    if (hostRef.current && !shadowRoot) {
      const root = hostRef.current.attachShadow({ mode: 'closed' });
      setShadowRoot(root);
    }
  }, [shadowRoot]);

  return (
    <div
      ref={hostRef}
      data-test='support-chat_shadow-root'
      style={{ zIndex: Z_INDICES.supportChat }}
    >
      {shadowRoot &&
        createPortal(
          <StyleSheetManager target={shadowRoot}>
            <SupportChatHelper
              {...rest}
              customReportBugCallback={() => {
                if (sentryFeedbackIntegration) {
                  sentryFeedbackIntegration.createForm().then((form) => {
                    form.appendToDom();
                    form.open();
                  });
                }
              }}
              isActiveOrganization={organization?.attributes.status === OrganizationStatus.Active}
            />
          </StyleSheetManager>,
          shadowRoot,
        )}
    </div>
  );
}
